import { useCallback, useEffect, useState } from 'react';
import type { ComposantEcran } from '../../app/ecrans.js';
import { CIBLE_MIN, COULEURS } from '../../app/styles.js';
import { messageErreur } from './erreurs.js';
import { FILTRES } from './donnees.js';
import {
  adresseTache,
  chargerFile,
  compter,
  filtrer,
  libelleFiltre,
  sansLigne,
  terminerTache,
  type Filtre,
  type LigneFile,
} from './file-du-jour.js';
import { obtenirServices } from './services.js';

export interface PropsVueFileDuJour {
  lignes: readonly LigneFile[];
  filtre: Filtre;
  jourLibelle: string;
  chargement: boolean;
  erreur: string | null;
  onFiltre: (filtre: Filtre) => void;
  onAction: (ligne: LigneFile) => void;
  onTerminer?: (ligne: LigneFile) => void;
  /** Relance le chargement de la file (échec de chargement). */
  onReessayer?: () => void;
  /** Titre de l'écran dans le contrat (« File du jour », sans prénom : l'écran sert à quiconque l'ouvre). */
  titre?: string;
}

const bouton = (actif: boolean) =>
  ({
    minHeight: CIBLE_MIN,
    minWidth: CIBLE_MIN,
    padding: '0 16px',
    borderRadius: 8,
    border: `1px solid ${COULEURS.marine}`,
    background: actif ? COULEURS.marine : COULEURS.blanc,
    color: actif ? COULEURS.blanc : COULEURS.marine,
    font: 'inherit',
    cursor: 'pointer',
  }) as const;

export function VueFileDuJour({
  lignes,
  filtre,
  jourLibelle,
  chargement,
  erreur,
  onFiltre,
  onAction,
  onTerminer,
  onReessayer,
  titre,
}: PropsVueFileDuJour) {
  // Une erreur sans aucune ligne : la file n'a pas pu être lue, jamais « à jour ».
  const illisible = !chargement && erreur !== null && lignes.length === 0;
  const compteurs = compter(lignes);
  const visibles = filtrer(lignes, filtre);
  // Le filtre « comptes rendus » n'apparaît que lorsqu'il y a de quoi y ranger (la liste de la maquette reste à quatre).
  const filtres = FILTRES.filter((f) => f !== 'comptes_rendus' || compteurs.comptes_rendus > 0 || filtre === 'comptes_rendus');
  return (
    <section data-ecran="D02" aria-labelledby="titre-file" style={{ color: COULEURS.marineFonce }}>
      <p style={{ margin: 0, color: COULEURS.ardoise }}>{titre ? `${titre}. ${jourLibelle}` : jourLibelle}</p>
      <h1 id="titre-file" style={{ color: COULEURS.marine, fontSize: 28, margin: '4px 0 16px' }}>
        Votre file du jour
      </h1>
      {erreur ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      {chargement ? (
        <p role="status">Chargement de votre file du jour…</p>
      ) : illisible ? (
        <div>
          <p style={{ fontWeight: 600 }}>Votre file n’a pas pu être chargée</p>
          {onReessayer ? (
            <button type="button" style={bouton(true)} onClick={onReessayer}>
              Réessayer
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div role="group" aria-label="Filtrer la file du jour" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {filtres.map((f) => (
              <button key={f} type="button" aria-pressed={f === filtre} style={bouton(f === filtre)} onClick={() => onFiltre(f)}>
                {libelleFiltre(f, compteurs[f])}
              </button>
            ))}
          </div>
          {visibles.length === 0 ? (
            <p>{lignes.length === 0 ? 'Aucune tâche aujourd’hui. Votre file est à jour.' : 'Aucune tâche dans cette catégorie.'}</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {visibles.map((l) => (
                <li
                  key={l.id}
                  data-tache={l.id}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 12,
                    padding: 12,
                    border: `1px solid ${COULEURS.bordure}`,
                    borderRadius: 8,
                  }}
                >
                  <strong style={{ minWidth: 56 }}>{l.heure}</strong>
                  <span style={{ flex: '1 1 220px' }}>
                    {l.titre}
                    {l.statut ? <em style={{ display: 'block', color: COULEURS.ambre, fontStyle: 'normal' }}>{l.statut}</em> : null}
                    {l.echeance ? (
                      <em style={{ display: 'block', color: COULEURS.ardoise, fontStyle: 'normal' }}>Échéance le {l.echeance}</em>
                    ) : null}
                  </span>
                  {l.badge ? <span style={{ color: COULEURS.ardoise }}>{l.badge}</span> : null}
                  <button type="button" style={bouton(false)} onClick={() => onAction(l)}>
                    {l.action}
                  </button>
                  <button
                    type="button"
                    aria-label={`Marquer faite : ${l.titre}`}
                    style={{
                      ...bouton(false),
                      border: 'none',
                      background: 'transparent',
                      color: COULEURS.ardoise,
                      textDecoration: 'underline',
                    }}
                    onClick={() => onTerminer?.(l)}
                  >
                    Marquer faite
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

const jourCourant = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guadeloupe' }).format(new Date());
const libelleJour = (): string => {
  const t = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'America/Guadeloupe',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export const FileDuJour: ComposantEcran = ({ ecran }) => {
  const [lignes, setLignes] = useState<LigneFile[]>([]);
  const [filtre, setFiltre] = useState<Filtre>('tout');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [essai, setEssai] = useState(0);

  useEffect(() => {
    let actif = true;
    setChargement(true);
    setErreur(null);
    chargerFile(obtenirServices().client, jourCourant())
      .then((l) => actif && setLignes(l))
      .catch((e: unknown) => actif && setErreur(messageErreur(e)))
      .finally(() => actif && setChargement(false));
    return () => {
      actif = false;
    };
  }, [essai]);

  const reessayer = useCallback(() => setEssai((n) => n + 1), []);

  const agir = useCallback((ligne: LigneFile) => window.location.assign(adresseTache(ligne)), []);

  // Une tâche terminée quitte la file à l'instant (échec typique de PER-2 : la tâche faite qui reste affichée).
  const terminer = useCallback((ligne: LigneFile) => {
    setErreur(null);
    terminerTache(obtenirServices().client, ligne)
      .then(() => setLignes((courantes) => sansLigne(courantes, ligne.id)))
      .catch((e: unknown) => setErreur(messageErreur(e)));
  }, []);

  return (
    <VueFileDuJour
      lignes={lignes}
      filtre={filtre}
      jourLibelle={libelleJour()}
      chargement={chargement}
      erreur={erreur}
      onFiltre={setFiltre}
      onAction={agir}
      onTerminer={terminer}
      onReessayer={reessayer}
      titre={ecran.titre}
    />
  );
};
